// CASE-01/OPS-01 — the exception inbox: catalog / order / statement / payout exceptions, with
// adoption into a durable case, triage (dismiss/snooze/reopen — visibility only) and named
// ownership (a specific operator + a deadline, independent of both `status` and triage).
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, RotateCcw, ShieldAlert, UserCheck } from "lucide-react";

import { ApiError, apiGet, apiPost } from "../api";
import { useAuth } from "../auth";
import { EXCEPTION_KINDS, isoToLocalInput, localInputToIso, newRequestId, operatorLabel } from "../cases";
import type {
  CaseDetail,
  CaseSummary,
  ExceptionItem,
  ExceptionKind,
  ExceptionQueue,
  OperatorOption,
} from "../types";
import OwnershipResolutionPanel from "./OwnershipResolution";

function stateLabel(value: string): string {
  return value.replace(/_/g, " ");
}

function CaseBadge({ c }: { c: CaseSummary | null }) {
  if (!c) return <span className="chip chip-risk">Not adopted</span>;
  const triage = c.dismissed_at ? "Dismissed" : c.snoozed_until ? "Snoozed" : null;
  return (
    <span className="cap-note" style={{ margin: 0 }}>
      {c.reference} · {c.status}
      {triage ? ` · ${triage}` : ""}
      {c.assigned_admin ? ` · owner: ${operatorLabel(c.assigned_admin)}` : c.assigned_admin_id ? " · owner: (removed operator)" : ""}
      {c.owner_deadline_at ? ` · due ${new Date(c.owner_deadline_at).toLocaleString()}` : ""}
    </span>
  );
}

function AdoptButton({ item, kind, onAdopted }: { item: ExceptionItem; kind: ExceptionKind; onAdopted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const adopt = async () => {
    setBusy(true);
    setErr(null);
    try {
      await apiPost("/exceptions/adopt", {
        client_request_id: newRequestId(),
        source_kind: kind,
        source_id: item.source.source_id,
        expected_fingerprint: item.source.fingerprint,
      });
      onAdopted();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Adoption outcome unknown — reload before retrying");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <button className="courier-btn" disabled={busy} onClick={() => void adopt()}>
        <Check size={12} /> {busy ? "Adopting…" : "Adopt into a case"}
      </button>
      {err && <p className="dlq-error">{err}</p>}
    </div>
  );
}

function CaseDetailPanel({
  caseId,
  operators,
  canAct,
  onClose,
  onChanged,
}: {
  caseId: string;
  operators: OperatorOption[];
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState<"open" | "resolved" | "closed">("open");
  const [ownerId, setOwnerId] = useState("");
  const [deadline, setDeadline] = useState("");
  const [snoozeUntil, setSnoozeUntil] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiGet<CaseDetail>(`/cases/${caseId}`);
      setDetail(d);
      setStatus(d.case.status);
      setOwnerId(d.case.assigned_admin_id || "");
      setDeadline(isoToLocalInput(d.case.owner_deadline_at));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load the case");
    }
  }, [caseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = () => {
    void load();
    onChanged();
  };

  const sendReply = async () => {
    if (!detail || !reply.trim()) return;
    setBusy("reply");
    try {
      await apiPost(`/cases/${caseId}/messages`, {
        client_request_id: newRequestId(),
        expected_version: detail.case.version,
        kind: "reply",
        body: reply.trim(),
      });
      setReply("");
      refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Reply outcome unknown — reload before retrying");
    } finally {
      setBusy(null);
    }
  };

  const changeStatus = async () => {
    if (!detail || status === detail.case.status) return;
    setBusy("status");
    try {
      await apiPost(`/cases/${caseId}/messages`, {
        client_request_id: newRequestId(),
        expected_version: detail.case.version,
        kind: "status",
        status,
      });
      refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Status change outcome unknown — reload before retrying");
    } finally {
      setBusy(null);
    }
  };

  const triage = async (action: "dismiss" | "snooze" | "reopen") => {
    if (!detail) return;
    if (action === "snooze" && !snoozeUntil) {
      setErr("Pick a snooze-until time first");
      return;
    }
    setBusy(action);
    try {
      await apiPost(`/cases/${caseId}/triage`, {
        client_request_id: newRequestId(),
        expected_version: detail.case.version,
        action,
        ...(action === "snooze" ? { snooze_until: localInputToIso(snoozeUntil) } : {}),
      });
      refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Triage outcome unknown — reload before retrying");
    } finally {
      setBusy(null);
    }
  };

  const assign = async (clear: boolean) => {
    if (!detail) return;
    setBusy(clear ? "clear" : "assign");
    try {
      await apiPost(`/cases/${caseId}/assign`, {
        client_request_id: newRequestId(),
        expected_version: detail.case.version,
        owner_admin_id: clear ? null : ownerId || null,
        deadline_at: clear ? null : localInputToIso(deadline),
      });
      if (clear) {
        setOwnerId("");
        setDeadline("");
      }
      refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Assignment outcome unknown — reload before retrying");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="lane dlq-lane">
      <header className="lane-head">
        <span>Case {detail?.case.reference || caseId}</span>
        <button className="courier-btn" onClick={onClose}>Close</button>
      </header>
      <div className="lane-body">
        {err && <div className="banner-err">{err}</div>}
        {!detail ? (
          <p className="lane-empty">Loading…</p>
        ) : (
          <>
            <p className="cap-note">
              {detail.case.subject} · status {detail.case.status} · v{detail.case.version}
            </p>

            <div className="recover-step">
              <p className="cap-note">Messages</p>
              {detail.messages.map((m) => (
                <div key={m.id} className="dlq-row">
                  <span className="dlq-status">{m.kind}</span>
                  <div className="dlq-main">
                    <span className="dlq-event">{m.actor_type}</span>
                    <span className="dlq-sub">{m.body}</span>
                  </div>
                  <div className="dlq-meta">
                    <span className="dlq-time">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>

            <OwnershipResolutionPanel
              caseId={caseId}
              detail={detail}
              canAct={canAct}
              onResolved={refresh}
              onReloadRequested={refresh}
            />

            {canAct && (
              <>
                <div className="recover-step">
                  <p className="cap-note">Reply</p>
                  <textarea
                    className="recover-textarea"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Add a note visible on this case"
                  />
                  <div className="recover-actions">
                    <button className="courier-btn" disabled={busy === "reply" || !reply.trim()} onClick={() => void sendReply()}>
                      {busy === "reply" ? "Sending…" : "Send reply"}
                    </button>
                  </div>
                </div>

                <div className="recover-step">
                  <p className="cap-note">Status (the sole authoritative-repair signal)</p>
                  <div className="recover-lookup">
                    <select
                      className="recover-input"
                      value={status}
                      onChange={(e) => setStatus(e.target.value as typeof status)}
                    >
                      <option value="open">open</option>
                      <option value="resolved">resolved</option>
                      <option value="closed">closed</option>
                    </select>
                    <button className="courier-btn" disabled={busy === "status" || status === detail.case.status} onClick={() => void changeStatus()}>
                      {busy === "status" ? "Saving…" : "Set status"}
                    </button>
                  </div>
                </div>

                <div className="recover-step">
                  <p className="cap-note">Inbox visibility (never a repair) — dismiss / snooze / reopen</p>
                  <div className="recover-lookup">
                    <button className="courier-btn" disabled={!!busy} onClick={() => void triage("dismiss")}>
                      {busy === "dismiss" ? "…" : "Dismiss"}
                    </button>
                    <input
                      type="datetime-local"
                      className="recover-input"
                      value={snoozeUntil}
                      onChange={(e) => setSnoozeUntil(e.target.value)}
                    />
                    <button className="courier-btn" disabled={!!busy} onClick={() => void triage("snooze")}>
                      {busy === "snooze" ? "…" : "Snooze until"}
                    </button>
                    <button className="courier-btn" disabled={!!busy} onClick={() => void triage("reopen")}>
                      {busy === "reopen" ? "…" : "Reopen in inbox"}
                    </button>
                  </div>
                </div>

                <div className="recover-step">
                  <p className="cap-note">
                    <UserCheck size={11} /> Named owner and deadline — accountability bookkeeping, distinct from
                    status and from inbox visibility above.
                  </p>
                  <div className="recover-lookup">
                    <select className="recover-input" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                      <option value="">Unassigned</option>
                      {operators.map((op) => (
                        <option key={op.id} value={op.id}>{operatorLabel(op)} ({op.role})</option>
                      ))}
                    </select>
                    <input
                      type="datetime-local"
                      className="recover-input"
                      value={deadline}
                      onChange={(e) => setDeadline(e.target.value)}
                    />
                    <button className="courier-btn" disabled={busy === "assign"} onClick={() => void assign(false)}>
                      {busy === "assign" ? "Saving…" : "Assign"}
                    </button>
                    <button className="courier-btn" disabled={busy === "clear"} onClick={() => void assign(true)}>
                      {busy === "clear" ? "…" : "Clear"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default function Exceptions() {
  const { user } = useAuth();
  const canAct = user?.role === "owner" || user?.role === "admin";

  const [kind, setKind] = useState<ExceptionKind>("catalog_publication");
  const [queue, setQueue] = useState<ExceptionQueue | null>(null);
  const [offset, setOffset] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState<OperatorOption[]>([]);
  const [openCase, setOpenCase] = useState<string | null>(null);

  const load = useCallback(async (k: ExceptionKind, off: number) => {
    setLoading(true);
    try {
      const q = await apiGet<ExceptionQueue>(`/exceptions?kind=${k}&offset=${off}`);
      setQueue(q);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load the exception queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(kind, offset);
  }, [kind, offset, load]);

  useEffect(() => {
    apiGet<{ operators: OperatorOption[] }>("/operators")
      .then((r) => setOperators(r.operators))
      .catch(() => setOperators([]));
  }, []);

  const switchKind = (k: ExceptionKind) => {
    setKind(k);
    setOffset(0);
  };

  return (
    <div className="board-page">
      <div className="board-header">
        <h1>Exceptions</h1>
      </div>
      <p className="cap-note">
        Catalog / order / statement / payout exceptions observed locally — never a provider repair.
        Adopt one into a durable case, then own it: a status change, inbox triage, and a named
        owner + deadline are three independent things.
      </p>

      <div className="dlq-tabs">
        {EXCEPTION_KINDS.map((k) => (
          <button
            key={k.value}
            className={`dlq-tab ${kind === k.value ? "active" : ""}`}
            onClick={() => switchKind(k.value)}
          >
            {k.label}
          </button>
        ))}
      </div>

      {err && <div className="banner-err">{err}</div>}

      <section className="lane dlq-lane">
        <header className="lane-head">
          <span>{loading ? "Loading…" : `${queue?.items.length ?? 0} shown`}</span>
        </header>
        <div className="lane-body">
          {(queue?.items.length ?? 0) === 0 && !loading ? (
            <p className="lane-empty">Nothing here</p>
          ) : (
            queue?.items.map((item) => (
              <div className="dlq-row" key={item.issue_id}>
                <span className={`dlq-status ${item.link_state !== "linked" ? "dlq-status-dead_letter" : ""}`}>
                  {stateLabel(item.source.state)}
                </span>
                <div className="dlq-main">
                  <span className="dlq-event">
                    {stateLabel(item.source.reason)}
                    {item.source.state === "held_unknown" && (
                      <span className="dlq-error"> <ShieldAlert size={11} /> </span>
                    )}
                  </span>
                  <span className="dlq-sub">vendor {item.source.vendor_id.slice(0, 8)} · source {item.source.source_id.slice(0, 8)}</span>
                  <CaseBadge c={item.case} />
                </div>
                <div className="dlq-meta">
                  {item.link_state === "linked" && item.case ? (
                    <button className="courier-btn" onClick={() => setOpenCase(item.case!.id)}>
                      View case
                    </button>
                  ) : canAct ? (
                    <AdoptButton item={item} kind={kind} onAdopted={() => void load(kind, offset)} />
                  ) : (
                    <span className="cap-note"><AlertTriangle size={11} /> Owner/admin can adopt</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        {queue?.page && (queue.page.has_more || offset > 0) && (
          <div className="recover-actions" style={{ padding: "8px 12px" }}>
            <button className="courier-btn" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - queue.page.limit))}>
              Previous
            </button>
            <button className="courier-btn" disabled={!queue.page.has_more || loading} onClick={() => setOffset(queue.page.next_offset ?? offset)}>
              <RotateCcw size={12} /> Next page
            </button>
          </div>
        )}
      </section>

      {openCase && (
        <CaseDetailPanel
          caseId={openCase}
          operators={operators}
          canAct={canAct}
          onClose={() => setOpenCase(null)}
          onChanged={() => void load(kind, offset)}
        />
      )}
    </div>
  );
}
